import React, { useMemo, useState } from 'react';
import { Button, Chip, CircularProgress, IconButton, MenuItem, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../modules/store/hooks';
import { marketingPersonApi, PaymentKind } from '../../../modules/services/marketingPersonService';
import { downloadMarketingPaymentsExcel } from '../../../common/downloadMarketingPersonsExcel';
import translate from '../../../i18n/translate';
import useStyles from './useStyles';

const PaymentsReportScreen: React.FC = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const language = useAppSelector((state) => state.auth.language);
  const langCode = language?.split('-')?.[0] || 'en';

  const prevMonthKey = dayjs().subtract(1, 'month').format('YYYY-MM');
  const [kind, setKind] = useState<'all' | PaymentKind>('all');
  const [monthFrom, setMonthFrom] = useState<string>(prevMonthKey);
  const [monthTo, setMonthTo] = useState<string>(prevMonthKey);
  const [search, setSearch] = useState<string>('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const filter = useMemo(() => ({
    kind: kind === 'all' ? undefined : kind,
    monthFrom: monthFrom || undefined,
    monthTo: monthTo || undefined,
    search: search || undefined,
  }), [kind, monthFrom, monthTo, search]);

  const { data, isFetching, refetch } = marketingPersonApi.useGetMarketingPaymentsReportQuery(filter);
  const [deleteMarketingPayment] = marketingPersonApi.useDeleteMarketingPaymentMutation();

  const summary = data?.summary;
  const payments = data?.payments || [];

  const handleDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      await deleteMarketingPayment({ paymentId: confirmDeleteId, language: langCode }).unwrap();
      setConfirmDeleteId(null);
      refetch();
    } catch (e) {
      console.error('Failed to delete payment', e);
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', flex: 0.4, minWidth: 60 },
    { field: 'month', headerName: translate('marketingPersonsPaymentMonth'), flex: 0.6, minWidth: 90 },
    {
      field: 'kind',
      headerName: translate('marketingPersonsPaymentKind'),
      flex: 0.6, minWidth: 110,
      renderCell: (params: any) => {
        const k = params.row.kind;
        const label = k === 'event'
          ? translate('marketingPersonsPaymentKindEvent')
          : k === 'referral'
            ? translate('marketingPersonsPaymentKindReferral')
            : translate('marketingPersonsPaymentKindOrganizer');
        const color = k === 'event' ? 'info' : k === 'referral' ? 'success' : 'warning';
        return <Chip size="small" label={label} color={color as any} variant="outlined" />;
      },
    },
    { field: 'marketingPersonName', headerName: translate('marketingPersonsGridName'), flex: 1.2, minWidth: 130 },
    { field: 'marketingPersonPhone', headerName: translate('marketingPersonsGridPhone'), flex: 1, minWidth: 120 },
    { field: 'organizerName', headerName: translate('marketingPersonsGridOrganizerName'), flex: 1.2, minWidth: 130 },
    {
      field: 'eventName',
      headerName: translate('marketingPersonsGridEventName'),
      flex: 1.4, minWidth: 150,
      renderCell: (params: any) => <span>{params.row.eventName || '-'}</span>,
    },
    {
      field: 'amount',
      headerName: translate('marketingPersonsPaymentAmount'),
      flex: 0.7, minWidth: 90,
      renderCell: (params: any) => <Typography variant="body2" color="success.main" fontWeight="bold">₪{(params.row.amount || 0).toLocaleString()}</Typography>,
    },
    {
      field: 'paidAt',
      headerName: translate('marketingPersonsPaymentPaidAt'),
      flex: 0.9, minWidth: 120,
      renderCell: (params: any) => <span>{params.row.paidAt ? new Date(params.row.paidAt).toLocaleDateString() : '-'}</span>,
    },
    {
      field: 'createDate',
      headerName: translate('marketingPersonsPaymentCreatedAt'),
      flex: 0.9, minWidth: 120,
      renderCell: (params: any) => <span>{params.row.createDate ? new Date(params.row.createDate).toLocaleDateString() : '-'}</span>,
    },
    { field: 'note', headerName: translate('marketingPersonsPaymentNote'), flex: 1.4, minWidth: 150 },
    {
      field: 'actions',
      headerName: translate('marketingPersonsGridActions'),
      flex: 0.6, minWidth: 80,
      sortable: false,
      renderCell: (params: any) => (
        <IconButton size="small" color="error" onClick={() => setConfirmDeleteId(params.row.id)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <div className={classes.Wrapper}>
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Tabs
        value={2}
        onChange={(_, v) => {
          if (v === 0) navigate('/marketing-persons/all');
          if (v === 1) navigate('/marketing-persons/payouts');
        }}
        sx={{ mt: 1, mb: 2, ml: 1, '& .MuiTab-root': { color: 'warning.main' }, '& .Mui-selected': { color: 'warning.dark' } }}
        TabIndicatorProps={{ sx: { backgroundColor: 'warning.main' } }}
      >
        <Tab label={translate('marketingPersonsTabPersons')} />
        <Tab label="Payouts" />
        <Tab label={translate('headerOpenMarketingPayments')} />
      </Tabs>

      <div className={classes.FilterRow}>
        <TextField
          size="small"
          color="warning"
          label={translate('marketingPersonsSearch')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 220 }}
        />
        <DatePicker
          label={translate('marketingPersonsPaymentMonthFrom')}
          views={['year', 'month']}
          openTo="month"
          value={monthFrom ? dayjs(`${monthFrom}-01`) : null}
          onChange={(value: Dayjs | null) => setMonthFrom(value ? value.format('YYYY-MM') : '')}
          renderInput={(params) => <TextField {...params} size="small" color="warning" sx={{ minWidth: 180 }} />}
        />
        <DatePicker
          label={translate('marketingPersonsPaymentMonthTo')}
          views={['year', 'month']}
          openTo="month"
          value={monthTo ? dayjs(`${monthTo}-01`) : null}
          onChange={(value: Dayjs | null) => setMonthTo(value ? value.format('YYYY-MM') : '')}
          renderInput={(params) => <TextField {...params} size="small" color="warning" sx={{ minWidth: 180 }} />}
        />
        <TextField
          select
          size="small"
          color="warning"
          label={translate('marketingPersonsPaymentKind')}
          value={kind}
          onChange={(e) => setKind(e.target.value as any)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="all">{translate('marketingPersonsAll')}</MenuItem>
          <MenuItem value="event">{translate('marketingPersonsPaymentKindEvent')}</MenuItem>
          <MenuItem value="organizer">{translate('marketingPersonsPaymentKindOrganizer')}</MenuItem>
          <MenuItem value="referral">{translate('marketingPersonsPaymentKindReferral')}</MenuItem>
        </TextField>
        <Button
          color="warning"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={() => downloadMarketingPaymentsExcel(filter)}
        >
          {translate('marketingPersonsDownloadAllExcel')}
        </Button>
        {(search || monthFrom || monthTo || kind !== 'all') && (
          <Tooltip title={translate('marketingPersonsClearFilters')}>
            <IconButton size="small" color="warning" onClick={() => {
              setSearch('');
              setMonthFrom('');
              setMonthTo('');
              setKind('all');
            }}>
              <FilterAltOffIcon />
            </IconButton>
          </Tooltip>
        )}
      </div>

      <div className={classes.SummarySection}>
        <Typography variant="h6" color="warning.main">
          {translate('marketingPersonsReportSummary')}
        </Typography>
        <div className={classes.SummaryStats}>
          <div className={classes.StatCard}>
            <Typography variant="body2" color="textSecondary">{translate('marketingPersonsPaymentsTotalCount')}</Typography>
            <Typography variant="h6" color="warning.main">{summary?.count ?? 0}</Typography>
          </div>
          <div className={classes.StatCard}>
            <Typography variant="body2" color="textSecondary">{translate('marketingPersonsPaymentsTotalAmount')}</Typography>
            <Typography variant="h6" color="success.main">₪{(summary?.totalAmount ?? 0).toLocaleString()}</Typography>
          </div>
          <div className={classes.StatCard}>
            <Typography variant="body2" color="textSecondary">{translate('marketingPersonsPaymentKindEvent')}</Typography>
            <Typography variant="h6" color="warning.main">{summary?.eventPaymentsCount ?? 0}</Typography>
          </div>
          <div className={classes.StatCard}>
            <Typography variant="body2" color="textSecondary">{translate('marketingPersonsPaymentKindOrganizer')}</Typography>
            <Typography variant="h6" color="warning.main">{summary?.organizerPaymentsCount ?? 0}</Typography>
          </div>
        </div>
      </div>

      {isFetching ? (
        <CircularProgress color="warning" />
      ) : (
        <DataGrid
          sx={{
            backgroundColor: '#ede9e4',
            borderRadius: '0px',
            border: 'none',
            '& .MuiDataGrid-virtualScroller': { marginTop: '0 !important' },
            '& .MuiDataGrid-main': { overflow: 'visible' },
            '.MuiDataGrid-columnHeaders': { backgroundColor: 'warning.main', position: 'sticky', zIndex: 2, borderRadius: '0px', border: '0px' },
            '.MuiDataGrid-columnHeaderTitleContainer': { color: '#fff', fontSize: '15px', padding: '10px', display: 'flex', alignContent: 'center', justifyContent: 'center' },
            '.MuiDataGrid-cell': { border: '1px solid #fc7f03', padding: '5px', display: 'flex', alignContent: 'center', justifyContent: 'center' },
            '.MuiDataGrid-footerContainer': { border: '1px solid #fc7f03' },
            '.MuiDataGrid-footerContainer p': { margin: '0px' },
            '.MuiDataGrid-columnHeaderTitle': { overflow: 'visible', lineHeight: '1.43rem', whiteSpace: 'normal' },
          }}
          rows={payments}
          columns={columns as unknown as GridColDef[]}
          getRowHeight={() => 'auto'}
          rowsPerPageOptions={[10, 25, 50]}
          autoHeight
          density="comfortable"
        />
      )}

      {confirmDeleteId !== null && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300,
          }}
          onClick={() => setConfirmDeleteId(null)}
        >
          <div
            style={{ background: '#fff', padding: 24, borderRadius: 8, minWidth: 320 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography variant="h6" color="warning.main">{translate('marketingPersonsPaymentDeleteTitle')}</Typography>
            <Typography sx={{ my: 2 }}>{translate('marketingPersonsPaymentDeleteText')}</Typography>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button color="warning" onClick={() => setConfirmDeleteId(null)}>{translate('marketingPersonsCancel')}</Button>
              <Button color="error" variant="contained" onClick={handleDelete}>{translate('marketingPersonsDelete')}</Button>
            </div>
          </div>
        </div>
      )}
    </LocalizationProvider>
    </div>
  );
};

export default PaymentsReportScreen;
